import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, clearToken, getToken, setToken } from '../api';
import type { CustomerUser } from '../types';

interface AuthContextValue {
  user: CustomerUser | null;
  loading: boolean;
  /** Resolves with a challenge when the account has 2FA, instead of signing in. */
  login: (email: string, password: string) => Promise<{ mfaChallenge?: string }>;
  completeMfaLogin: (challenge: string, code: string) => Promise<void>;
  register: (payload: {
    companyName: string;
    billingEmail: string;
    name: string;
    email: string;
    password: string;
  }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CustomerUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    auth
      .me()
      .then(setUser)
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const result = await auth.login(email, password);

    // With 2FA on, the password step yields only a challenge — no session yet.
    if (result.mfaRequired && result.challenge) {
      return { mfaChallenge: result.challenge };
    }
    if (!result.token || !result.user) {
      throw new Error('Unerwartete Antwort beim Anmelden.');
    }
    setToken(result.token);
    setUser(result.user);
    return {};
  }

  async function completeMfaLogin(challenge: string, code: string) {
    const { token, user } = await auth.verifyMfa(challenge, code);
    setToken(token);
    setUser(user);
  }

  async function register(payload: {
    companyName: string;
    billingEmail: string;
    name: string;
    email: string;
    password: string;
  }) {
    const { token, user } = await auth.register(payload);
    setToken(token);
    setUser(user);
  }

  function logout() {
    clearToken();
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, completeMfaLogin, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
